import axios from 'axios';
import { REQUEST_URL } from '../config/urls';

export const ADD_MESSAGE = 'CHATBOT_ADD_MESSAGE';
export const SET_TYPING = 'CHATBOT_SET_TYPING';
export const SET_ERROR = 'CHATBOT_SET_ERROR';
export const CLEAR_MESSAGES = 'CHATBOT_CLEAR_MESSAGES';
export const LOAD_HISTORY = 'CHATBOT_LOAD_HISTORY';
export const SET_OPEN = 'CHATBOT_SET_OPEN';

/**
 * Função auxiliar para retry com backoff exponencial
 */
const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      // Não fazer retry em erros 4xx (exceto timeout)
      if (error.response?.status >= 400 && error.response?.status < 500 && error.code !== 'ECONNABORTED') {
        throw error;
      }

      // Se for a última tentativa, lançar o erro
      if (attempt === maxRetries) {
        throw error;
      }

      // Calcular delay com backoff exponencial
      const delay = baseDelay * Math.pow(2, attempt - 1);
      console.log(`Tentativa ${attempt} falhou. Tentando novamente em ${delay}ms...`);

      // Aguardar antes de tentar novamente
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

/**
 * Enviar mensagem para o chatbot
 */
export const sendMessage = (message) => {
  return async (dispatch, getState) => {
    const state = getState();
    const usuario = state.usuario.autenticado;
    const contrato = state.contrato.especifico;

    if (!usuario || !usuario.id) {
      dispatch({
        type: SET_ERROR,
        payload: 'Usuário não autenticado'
      });
      return;
    }

    // Adicionar mensagem do usuário
    dispatch({
      type: ADD_MESSAGE,
      payload: {
        role: 'user',
        content: message,
        isBot: false,
        timestamp: new Date().toISOString()
      }
    });

    // Mostrar indicador de digitação
    dispatch({ type: SET_TYPING, payload: true });

    try {
      const token = sessionStorage.getItem('token');

      // Usar retry automático para a requisição
      const response = await retryWithBackoff(
        async () => {
          return await axios.post(
            `${REQUEST_URL}/chatbot/message`,
            {
              message,
              userId: usuario.id,
              contratoId: contrato?.id
            },
            {
              headers: {
                'Authorization': token
              },
              timeout: 60000 // 60 segundos
            }
          );
        },
        3, // Máximo 3 tentativas
        1000 // Delay inicial de 1 segundo
      );

      // Adicionar resposta do bot
      dispatch({
        type: ADD_MESSAGE,
        payload: {
          role: 'assistant',
          content: response.data.message,
          isBot: true,
          timestamp: response.data.timestamp,
          functionCalled: response.data.functionCalled
        }
      });

    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);

      let errorMessage = 'Desculpe, ocorreu um erro ao processar sua mensagem. 😔';

      if (error.code === 'ECONNABORTED') {
        errorMessage = 'A requisição demorou muito tempo. Por favor, tente novamente.';
      } else if (error.response?.status === 400) {
        errorMessage = 'Erro ao enviar mensagem. Verifique os dados e tente novamente.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Usuário não encontrado. Por favor, faça login novamente.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Erro no servidor. Por favor, tente novamente em alguns instantes.';
      }

      // Mensagem de erro amigável
      dispatch({
        type: ADD_MESSAGE,
        payload: {
          role: 'assistant',
          content: errorMessage,
          isBot: true,
          timestamp: new Date().toISOString(),
          error: true
        }
      });

      dispatch({
        type: SET_ERROR,
        payload: error.response?.data?.error || 'Erro ao enviar mensagem'
      });
    } finally {
      dispatch({ type: SET_TYPING, payload: false });
    }
  };
};

/**
 * Carregar histórico da conversa
 */
export const loadHistory = () => {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const usuario = state.usuario.autenticado;

      if (!usuario || !usuario.id) return;

      const token = sessionStorage.getItem('token');

      const response = await axios.get(
        `${REQUEST_URL}/chatbot/history/${usuario.id}`,
        {
          headers: {
            'Authorization': token
          }
        }
      );

      dispatch({
        type: LOAD_HISTORY,
        payload: response.data.messages || []
      });

    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
  };
};

/**
 * Limpar histórico da conversa
 */
export const clearConversation = () => {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const usuario = state.usuario.autenticado;

      if (!usuario || !usuario.id) return;

      const token = sessionStorage.getItem('token');

      await axios.post(
        `${REQUEST_URL}/chatbot/clear`,
        { userId: usuario.id },
        {
          headers: {
            'Authorization': token
          }
        }
      );

      dispatch({ type: CLEAR_MESSAGES });

    } catch (error) {
      console.error('Erro ao limpar conversa:', error);
    }
  };
};

/**
 * Definir se o chat está aberto ou fechado
 */
export const setChatOpen = (isOpen) => {
  return {
    type: SET_OPEN,
    payload: isOpen
  };
};
